import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, compressImage, formatApiErrorDetail } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { contrastRatio } from "@/lib/color";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Palette,
  Eye,
  Rocket,
  Copy,
  Trash2,
  Download,
  Upload,
  Sparkles,
  History,
  Save,
  Image as ImageIcon,
  AlertTriangle,
  RotateCcw,
  CheckCircle2,
  LayoutDashboard,
  PanelLeft,
  Type,
  Box,
  Wand2,
  Gauge,
  Layers3,
  Monitor,
  Sun,
  Moon,
  Settings2,
  Zap,
} from "lucide-react";


/* ============================================================
   TOKENS
============================================================ */

const TOKEN_GROUPS = {
  Fundos: [
    "background",
    "card",
    "popover",
    "secondary",
    "muted",
    "accent",
  ],

  Textos: [
    "foreground",
    "card_foreground",
    "muted_foreground",
    "secondary_foreground",
    "accent_foreground",
    "popover_foreground",
  ],

  "Botões e destaque": [
    "primary",
    "primary_foreground",
    "destructive",
    "destructive_foreground",
    "ring",
  ],

  "Bordas e campos": [
    "border",
    "input",
  ],

  Gráficos: [
    "chart_1",
    "chart_2",
    "chart_3",
    "chart_4",
    "chart_5",
  ],
};


/* ============================================================
   FONTES
============================================================ */

const FONTS = [
  "Inter",
  "Outfit",
  "Manrope",
  "Sora",
  "IBM Plex Sans",
  "Work Sans",
  "DM Sans",
  "Space Grotesk",
  "Public Sans",
  "Figtree",
  "Plus Jakarta Sans",
  "Poppins",
  "Nunito Sans",
  "Montserrat",
  "Roboto",
  "Barlow",
  "Barlow Condensed",
  "Merriweather",
  "Libre Baskerville",
  "Playfair Display",
  "Cormorant Garamond",
  "JetBrains Mono",
  "Fira Code",
];


const RADIUS_OPTIONS = [
  "0rem",
  "0.125rem",
  "0.25rem",
  "0.375rem",
  "0.5rem",
  "0.625rem",
  "0.75rem",
  "0.875rem",
  "1rem",
  "1.125rem",
  "1.25rem",
  "1.5rem",
  "1.75rem",
  "2rem",
];


const FONT_SIZE_OPTIONS = [
  "13px",
  "14px",
  "15px",
  "16px",
  "17px",
  "18px",
];


const FONT_WEIGHT_OPTIONS = [
  { value: 400, label: "400 — Regular" },
  { value: 500, label: "500 — Medium" },
  { value: 600, label: "600 — Semibold" },
  { value: 700, label: "700 — Bold" },
  { value: 800, label: "800 — Extra Bold" },
];


const WIDTH_OPTIONS = [
  "220px",
  "240px",
  "260px",
  "280px",
  "300px",
  "320px",
];


const HEADER_HEIGHT_OPTIONS = [
  "56px",
  "60px",
  "64px",
  "68px",
  "72px",
  "76px",
];


const ANIMATION_SPEED_OPTIONS = [
  { value: "instantanea", label: "Instantânea" },
  { value: "rapida", label: "Rápida" },
  { value: "normal", label: "Normal" },
  { value: "suave", label: "Suave" },
  { value: "cinematica", label: "Cinemática" },
];


const SHADOW_OPTIONS = [
  { value: "nenhuma", label: "Nenhuma" },
  { value: "suave", label: "Suave" },
  { value: "media", label: "Média" },
  { value: "forte", label: "Forte" },
  { value: "flutuante", label: "Flutuante" },
];


const NAVIGATION_STYLES = [
  { value: "sidebar", label: "Sidebar" },
  { value: "topbar", label: "Barra superior" },
  { value: "hybrid", label: "Híbrida" },
];


const SIDEBAR_STYLES = [
  { value: "solida", label: "Sólida" },
  { value: "dark", label: "Dark" },
  { value: "glass", label: "Glass" },
  { value: "outlined", label: "Contornada" },
  { value: "floating", label: "Flutuante" },
];


const HEADER_STYLES = [
  { value: "solido", label: "Sólido" },
  { value: "glass", label: "Glass" },
  { value: "transparente", label: "Transparente" },
  { value: "elevado", label: "Elevado" },
];


const ACTIVE_INDICATORS = [
  { value: "barra", label: "Barra lateral" },
  { value: "pill", label: "Pill" },
  { value: "fundo", label: "Fundo" },
  { value: "borda", label: "Borda" },
  { value: "glow", label: "Glow" },
];


const HOVER_EFFECTS = [
  { value: "none", label: "Nenhum" },
  { value: "soft", label: "Suave" },
  { value: "lift", label: "Elevação" },
  { value: "glow", label: "Glow" },
  { value: "scale", label: "Escala" },
];


const CARD_STYLES = [
  { value: "flat", label: "Plano" },
  { value: "outlined", label: "Contornado" },
  { value: "elevado", label: "Elevado" },
  { value: "glass", label: "Glass" },
  { value: "floating", label: "Flutuante" },
];


const BUTTON_STYLES = [
  { value: "solid", label: "Sólido" },
  { value: "soft", label: "Soft" },
  { value: "outlined", label: "Contornado" },
  { value: "ghost", label: "Ghost" },
  { value: "gradient", label: "Gradiente" },
];


const INPUT_STYLES = [
  { value: "outlined", label: "Contornado" },
  { value: "filled", label: "Preenchido" },
  { value: "minimal", label: "Minimalista" },
  { value: "glass", label: "Glass" },
];


const TABLE_STYLES = [
  { value: "soft", label: "Soft" },
  { value: "outlined", label: "Contornada" },
  { value: "striped", label: "Listrada" },
  { value: "minimal", label: "Minimalista" },
];


const DENSITY_OPTIONS = [
  { value: "compacta", label: "Compacta" },
  { value: "confortavel", label: "Confortável" },
  { value: "espacosa", label: "Espaçosa" },
];


const VISUAL_STYLES = [
  { value: "moderno", label: "Moderno" },
  { value: "premium", label: "Premium" },
  { value: "florestal", label: "Florestal" },
  { value: "organico", label: "Orgânico" },
  { value: "tecnologico", label: "Tecnológico" },
  { value: "industrial", label: "Industrial" },
  { value: "minimalista", label: "Minimalista" },
];


const BRAND_EMPHASIS = [
  { value: "discreto", label: "Discreto" },
  { value: "medio", label: "Médio" },
  { value: "forte", label: "Forte" },
];


const AI_EXAMPLES = [
  "Criar um tema florestal premium, inspirado em uma floresta tropical, com verde profundo, tons naturais, glassmorphism discreto e navegação moderna.",

  "Criar um tema corporativo extremamente sofisticado, azul marinho, grafite e prata, com sidebar flutuante, cards elevados e transições suaves.",

  "Criar uma identidade tecnológica futurista em ciano e violeta, com glassmorphism, gradientes discretos, glow e animações fluidas.",

  "Criar uma identidade premium inspirada em hotéis de luxo, preto, champagne e dourado, com tipografia elegante e muito espaço visual.",

  "Criar um tema agro moderno inspirado em campo, floresta e terra, usando verdes orgânicos, bege e detalhes dourados.",

  "Criar um tema industrial para operação em campo, grafite, aço e laranja de sinalização, com interface compacta e altamente funcional.",
];


/* ============================================================
   HELPERS
============================================================ */

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}


function ensureThemeStructure(theme) {
  const source = deepClone(theme || {});

  source.radius = source.radius || "0.75rem";

  source.font_heading =
    source.font_heading ||
    source.typography?.font_heading ||
    "Outfit";

  source.font_body =
    source.font_body ||
    source.typography?.font_body ||
    "Inter";

  source.font_mono =
    source.font_mono ||
    source.typography?.font_mono ||
    "JetBrains Mono";

  source.typography = {
    font_heading: source.font_heading,
    font_body: source.font_body,
    font_mono: source.font_mono,
    font_display:
      source.typography?.font_display ||
      source.font_heading ||
      "Outfit",
    base_size:
      source.typography?.base_size ||
      "14px",
    line_height:
      source.typography?.line_height ||
      "1.5",
    heading_weight:
      source.typography?.heading_weight ??
      700,
    body_weight:
      source.typography?.body_weight ??
      400,
    medium_weight:
      source.typography?.medium_weight ??
      500,
    semibold_weight:
      source.typography?.semibold_weight ??
      600,
    bold_weight:
      source.typography?.bold_weight ??
      700,
    letter_spacing:
      source.typography?.letter_spacing ||
      "0",
    heading_letter_spacing:
      source.typography?.heading_letter_spacing ||
      "-0.02em",
  };

  source.navigation = {
    style:
      source.navigation?.style ||
      "sidebar",
    sidebar_style:
      source.navigation?.sidebar_style ||
      "solida",
    sidebar_mode:
      source.navigation?.sidebar_mode ||
      "expandida",
    sidebar_width:
      source.navigation?.sidebar_width ||
      "260px",
    sidebar_collapsed_width:
      source.navigation?.sidebar_collapsed_width ||
      "72px",
    header_style:
      source.navigation?.header_style ||
      "solido",
    header_height:
      source.navigation?.header_height ||
      "64px",
    menu_radius:
      source.navigation?.menu_radius ||
      source.radius ||
      "0.75rem",
    menu_item_height:
      source.navigation?.menu_item_height ||
      "42px",
    menu_icon_size:
      source.navigation?.menu_icon_size ||
      "18px",
    submenu_indent:
      source.navigation?.submenu_indent ||
      "14px",
    show_icons:
      source.navigation?.show_icons ??
      true,
    show_labels:
      source.navigation?.show_labels ??
      true,
    show_breadcrumbs:
      source.navigation?.show_breadcrumbs ??
      true,
    show_search:
      source.navigation?.show_search ??
      true,
    show_notifications:
      source.navigation?.show_notifications ??
      true,
    sticky_sidebar:
      source.navigation?.sticky_sidebar ??
      true,
    sticky_header:
      source.navigation?.sticky_header ??
      true,
    active_indicator:
      source.navigation?.active_indicator ||
      "barra",
    hover_effect:
      source.navigation?.hover_effect ||
      "soft",
  };

  source.components = {
    card_style:
      source.components?.card_style ||
      "elevado",
    button_style:
      source.components?.button_style ||
      "solid",
    input_style:
      source.components?.input_style ||
      "outlined",
    table_style:
      source.components?.table_style ||
      "soft",
    badge_style:
      source.components?.badge_style ||
      "soft",
    modal_style:
      source.components?.modal_style ||
      "elevado",
    dropdown_style:
      source.components?.dropdown_style ||
      "elevado",

    radius:
      source.components?.radius ||
      source.radius ||
      "0.75rem",

    card_radius:
      source.components?.card_radius ||
      "1rem",

    button_radius:
      source.components?.button_radius ||
      "0.625rem",

    input_radius:
      source.components?.input_radius ||
      "0.625rem",

    modal_radius:
      source.components?.modal_radius ||
      "1rem",

    badge_radius:
      source.components?.badge_radius ||
      "9999px",

    border_width:
      source.components?.border_width ||
      "1px",

    focus_width:
      source.components?.focus_width ||
      "2px",
  };

  source.effects = {
    shadow:
      source.effects?.shadow ||
      "suave",

    card_shadow:
      source.effects?.card_shadow ||
      "suave",

    header_shadow:
      source.effects?.header_shadow ||
      "nenhuma",

    sidebar_shadow:
      source.effects?.sidebar_shadow ||
      "suave",

    hover_lift:
      source.effects?.hover_lift ??
      true,

    hover_scale:
      source.effects?.hover_scale ??
      false,

    glass_enabled:
      source.effects?.glass_enabled ??
      false,

    glass_blur:
      source.effects?.glass_blur ||
      "12px",

    glass_opacity:
      source.effects?.glass_opacity ??
      0.82,

    backdrop_enabled:
      source.effects?.backdrop_enabled ??
      false,

    gradient_enabled:
      source.effects?.gradient_enabled ??
      false,

    animation_enabled:
      source.effects?.animation_enabled ??
      true,

    animation_speed:
      source.effects?.animation_speed ||
      "normal",
  };

  source.density = {
    mode:
      source.density?.mode ||
      "confortavel",

    page_padding:
      source.density?.page_padding ||
      "24px",

    section_gap:
      source.density?.section_gap ||
      "24px",

    card_padding:
      source.density?.card_padding ||
      "20px",

    table_row_height:
      source.density?.table_row_height ||
      "48px",

    form_gap:
      source.density?.form_gap ||
      "16px",

    menu_gap:
      source.density?.menu_gap ||
      "4px",
  };

  source.identity = {
    visual_style:
      source.identity?.visual_style ||
      "moderno",

    brand_emphasis:
      source.identity?.brand_emphasis ||
      "medio",

    logo_position:
      source.identity?.logo_position ||
      "sidebar",

    logo_size:
      source.identity?.logo_size ||
      "32px",

    favicon:
      source.identity?.favicon ||
      "",

    background_image:
      source.identity?.background_image ||
      "",

    background_pattern:
      source.identity?.background_pattern ||
      "",

    background_overlay:
      source.identity?.background_overlay ??
      0,
  };

  source.dark = source.dark || {};
  source.light = source.light || {};

  return source;
}


function buildThemePayload(theme) {
  const t = ensureThemeStructure(theme);

  return {
    name: t.name || "Tema personalizado",
    category: t.category || "Personalizados",

    radius:
      t.radius ||
      t.components?.radius ||
      "0.75rem",

    font_heading:
      t.font_heading ||
      t.typography?.font_heading ||
      "Outfit",

    font_body:
      t.font_body ||
      t.typography?.font_body ||
      "Inter",

    font_mono:
      t.font_mono ||
      t.typography?.font_mono ||
      "JetBrains Mono",

    typography: t.typography,
    navigation: t.navigation,
    components: t.components,
    effects: t.effects,
    density: t.density,
    identity: t.identity,

    dark: t.dark,
    light: t.light,
  };
}


/* ============================================================
   SWATCHES
============================================================ */

function Swatches({ theme }) {
  const keys = [
    "primary",
    "background",
    "card",
    "foreground",
    "chart_2",
  ];

  return (
    <div className="flex gap-1.5">
      {keys.map((key) => (
        <span
          key={key}
          className="h-5 w-5 rounded-full border border-border shadow-sm"
          style={{
            background:
              theme.dark?.[key] ||
              "#000000",
          }}
          title={key}
        />
      ))}
    </div>
  );
}


/* ============================================================
   COLOR FIELD
============================================================ */

function ColorField({
  label,
  value,
  onChange,
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value || "#000000"}
        onChange={(event) =>
          onChange(event.target.value)
        }
        className="
          h-8
          w-9
          rounded
          border
          border-border
          bg-transparent
          cursor-pointer
          p-0.5
        "
        data-testid={`color-${label}`}
      />

      <div className="min-w-0 flex-1">
        <p className="text-[11px] truncate capitalize">
          {label.replace(/_/g, " ")}
        </p>

        <p className="text-[10px] font-mono text-muted-foreground">
          {value || "—"}
        </p>
      </div>
    </div>
  );
}


/* ============================================================
   SELECT FIELD
============================================================ */

function SelectField({
  label,
  value,
  onChange,
  options,
  className = "w-48",
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">
        {label}
      </Label>

      <Select
        value={value}
        onValueChange={onChange}
      >
        <SelectTrigger className={className}>
          <SelectValue />
        </SelectTrigger>

        <SelectContent>
          {options.map((option) => {
            const item =
              typeof option === "string"
                ? {
                    value: option,
                    label: option,
                  }
                : option;

            return (
              <SelectItem
                key={item.value}
                value={String(item.value)}
              >
                {item.label}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}


/* ============================================================
   BOOLEAN FIELD
============================================================ */

function BooleanField({
  label,
  value,
  onChange,
  description,
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className="
        w-full
        flex
        items-center
        justify-between
        gap-3
        rounded-lg
        border
        border-border
        bg-background/50
        px-3
        py-2.5
        text-left
        transition-all
        hover:border-primary/40
      "
    >
      <div className="min-w-0">
        <p className="text-xs font-medium">
          {label}
        </p>

        {description && (
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {description}
          </p>
        )}
      </div>

      <span
        className={`
          relative
          shrink-0
          h-5
          w-9
          rounded-full
          transition-colors
          ${value
            ? "bg-primary"
            : "bg-muted"
          }
        `}
      >
        <span
          className={`
            absolute
            top-0.5
            h-4
            w-4
            rounded-full
            bg-white
            shadow-sm
            transition-transform
            ${value
              ? "translate-x-4"
              : "translate-x-0.5"
            }
          `}
        />
      </span>
    </button>
  );
}


/* ============================================================
   THEME PREVIEW
============================================================ */

function ThemeMiniPreview({
  theme,
  mode = "dark",
}) {
  const palette =
    theme?.[mode] ||
    theme?.dark ||
    {};

  const navigation =
    theme?.navigation ||
    {};

  const components =
    theme?.components ||
    {};

  const effects =
    theme?.effects ||
    {};

  const radius =
    components.card_radius ||
    theme?.radius ||
    "0.75rem";

  return (
    <div
      className="
        overflow-hidden
        rounded-xl
        border
        border-border
        shadow-sm
        relative
      "
      style={{
        background:
          palette.background ||
          "#0a0a10",
        color:
          palette.foreground ||
          "#ffffff",
      }}
    >
      <div className="flex h-28">
        <div
          className="w-16 p-2"
          style={{
            background:
              palette.card ||
              palette.background,
            borderRight:
              `1px solid ${
                palette.border ||
                "#333"
              }`,
          }}
        >
          <div
            className="h-2 w-7 rounded mb-3"
            style={{
              background:
                palette.primary,
            }}
          />

          {[1, 2, 3, 4].map(
            (item) => (
              <div
                key={item}
                className="h-1.5 rounded mb-2"
                style={{
                  width:
                    item === 1
                      ? "80%"
                      : "60%",
                  background:
                    palette.muted_foreground ||
                    palette.foreground,
                  opacity:
                    item === 1
                      ? 0.9
                      : 0.45,
                }}
              />
            ),
          )}
        </div>

        <div className="flex-1 p-3">
          <div
            className="h-2.5 w-24 rounded mb-2"
            style={{
              background:
                palette.foreground,
              opacity: 0.85,
            }}
          />

          <div
            className="h-1.5 w-40 rounded mb-4"
            style={{
              background:
                palette.muted_foreground ||
                palette.foreground,
              opacity: 0.45,
            }}
          />

          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3].map(
              (item) => (
                <div
                  key={item}
                  className="h-10 p-2"
                  style={{
                    background:
                      palette.card,
                    border:
                      `1px solid ${
                        palette.border ||
                        "#333"
                      }`,
                    borderRadius: radius,
                    boxShadow:
                      effects.card_shadow ===
                      "nenhuma"
                        ? "none"
                        : "0 4px 14px rgba(0,0,0,.12)",
                  }}
                >
                  <div
                    className="h-1.5 w-8 rounded"
                    style={{
                      background:
                        item === 1
                          ? palette.primary
                          : palette.muted_foreground,
                    }}
                  />
                </div>
              ),
            )}
          </div>
        </div>
      </div>

      <div
        className="absolute bottom-2 right-2 text-[8px] uppercase tracking-wider opacity-40"
      >
        {navigation.style || "sidebar"}
      </div>
    </div>
  );
}


/* ============================================================
   MAIN
============================================================ */

export default function VisualIdentity() {
  const {
    branding,
    refresh,
    preview,
    clearPreview,
  } = useBranding();

  const [data, setData] = useState({
    themes: [],
    active_theme_id: null,
    history: [],
  });

  const [previewing, setPreviewing] =
    useState(null);

  const [issues, setIssues] =
    useState(null);

  const [busy, setBusy] =
    useState(false);

  const [brandForm, setBrandForm] =
    useState(branding || {});

  const uploadRefs = {
    favicon: useRef(),
    logo_main: useRef(),
    logo_small: useRef(),
    login_bg: useRef(),
  };

  const importRef =
    useRef();

  const [editTheme, setEditTheme] =
    useState(null);

  const [editMode, setEditMode] =
    useState("dark");

  const [editorSection, setEditorSection] =
    useState("cores");

  const [aiPrompt, setAiPrompt] =
    useState("");

  const [aiProposal, setAiProposal] =
    useState(null);


  /* ==========================================================
     LOAD
  ========================================================== */

  const load = useCallback(
    async () => {
      try {
        const response =
          await api.get("/themes");

        setData(
          response.data,
        );
      } catch (err) {
        toast.error(
          formatApiErrorDetail(
            err.response?.data?.detail,
          ),
        );
      }
    },
    [],
  );


  useEffect(() => {
    load();
  }, [load]);


  useEffect(() => {
    setBrandForm(
      branding || {},
    );
  }, [branding]);


  useEffect(
    () => () => clearPreview(),
    [],
  );


  useEffect(() => {
    if (editTheme) {
      preview(editTheme);
      setPreviewing(editTheme);
    }
  }, [editTheme]); // eslint-disable-line


  /* ==========================================================
     PREVIEW
  ========================================================== */

  const startPreview = (
    theme,
  ) => {
    const normalized =
      ensureThemeStructure(
        theme,
      );

    preview(normalized);

    setPreviewing(
      normalized,
    );

    toast.info(
      `Pré-visualizando "${normalized.name || "tema"}" — nada foi publicado ainda`,
    );
  };


  const stopPreview = () => {
    clearPreview();
    setPreviewing(null);
  };


  /* ==========================================================
     PUBLISH
  ========================================================== */

  const publish = async (
    themeId,
  ) => {
    setBusy(true);

    try {
      const {
        data: response,
      } = await api.post(
        `/themes/${themeId}/publish`,
      );

      toast.success(
        response.warnings?.length
          ? `Tema publicado com ${response.warnings.length} aviso(s) de contraste`
          : "Tema publicado globalmente para todos os usuários",
      );

      stopPreview();

      await refresh();
      await load();
    } catch (err) {
      const detail =
        err.response?.data?.detail;

      if (
        detail?.issues
      ) {
        setIssues(
          detail.issues,
        );

        toast.error(
          detail.message ||
            "Tema reprovado na validação.",
        );
      } else {
        toast.error(
          formatApiErrorDetail(
            detail,
          ),
        );
      }
    } finally {
      setBusy(false);
    }
  };


  /* ==========================================================
     RESET
  ========================================================== */

  const resetDefault =
    async () => {
      setBusy(true);

      try {
        await api.post(
          "/themes/reset-default",
        );

        toast.success(
          "Tema padrão do sistema restaurado",
        );

        stopPreview();

        await refresh();
        await load();
      } catch (err) {
        toast.error(
          formatApiErrorDetail(
            err.response?.data?.detail,
          ),
        );
      } finally {
        setBusy(false);
      }
    };


  /* ==========================================================
     DUPLICATE
  ========================================================== */

  const duplicate =
    async (id) => {
      try {
        await api.post(
          `/themes/${id}/duplicate`,
        );

        toast.success(
          "Tema duplicado — agora editável",
        );

        await load();
      } catch (err) {
        toast.error(
          formatApiErrorDetail(
            err.response?.data?.detail,
          ),
        );
      }
    };


  /* ==========================================================
     DELETE
  ========================================================== */

  const removeTheme =
    async (id) => {
      if (
        !window.confirm(
          "Excluir este tema?",
        )
      ) {
        return;
      }

      try {
        await api.delete(
          `/themes/${id}`,
        );

        toast.success(
          "Tema excluído",
        );

        await load();
      } catch (err) {
        toast.error(
          formatApiErrorDetail(
            err.response?.data?.detail,
          ),
        );
      }
    };


  /* ==========================================================
     EXPORT
  ========================================================== */

  const exportTheme =
    async (
      id,
      name,
    ) => {
      try {
        const {
          data: theme,
        } = await api.get(
          `/themes/${id}/export`,
        );

        const blob =
          new Blob(
            [
              JSON.stringify(
                theme,
                null,
                2,
              ),
            ],
            {
              type:
                "application/json",
            },
          );

        const url =
          URL.createObjectURL(
            blob,
          );

        const anchor =
          document.createElement(
            "a",
          );

        anchor.href = url;

        anchor.download =
          `tema-${name
            .toLowerCase()
            .replace(
              /\s+/g,
              "-",
            )}.json`;

        anchor.click();

        URL.revokeObjectURL(
          url,
        );
      } catch (err) {
        toast.error(
          formatApiErrorDetail(
            err.response?.data?.detail,
          ),
        );
      }
    };


  /* ==========================================================
     IMPORT
  ========================================================== */

  const importTheme =
    (files) => {
      const file =
        files?.[0];

      if (!file) {
        return;
      }

      const reader =
        new FileReader();

      reader.onload =
        async () => {
          try {
            const theme =
              JSON.parse(
                reader.result,
              );

            const normalized =
              ensureThemeStructure(
                theme,
              );

            await api.post(
              "/themes/import",
              buildThemePayload(
                {
                  ...normalized,
                  name:
                    normalized.name ||
                    "Tema importado",
                  category:
                    "Personalizados",
                },
              ),
            );

            toast.success(
              "Tema importado com todas as configurações visuais",
            );

            await load();
          } catch (err) {
            toast.error(
              err?.response?.data?.detail
                ? formatApiErrorDetail(
                    err.response.data.detail,
                  )
                : "Arquivo de tema inválido",
            );
          }
        };

      reader.readAsText(
        file,
      );
    };


  /* ==========================================================
     BRANDING
  ========================================================== */

  const saveBranding =
    async () => {
      setBusy(true);

      try {
        await api.put(
          "/themes/branding",
          brandForm,
        );

        toast.success(
          "Identidade da marca publicada para todos os usuários",
        );

        await refresh();
      } catch (err) {
        toast.error(
          formatApiErrorDetail(
            err.response?.data?.detail,
          ),
        );
      } finally {
        setBusy(false);
      }
    };


  const uploadBrandImage =
    (key) =>
    async (files) => {
      if (
        !files?.[0]
      ) {
        return;
      }

      try {
        const base64 =
          await compressImage(
            files[0],
            key ===
            "favicon"
              ? 64
              : key ===
                "login_bg"
                ? 1600
                : 400,
            0.85,
          );

        setBrandForm(
          (current) => ({
            ...current,
            [key]:
              base64,
          }),
        );
      } catch {
        toast.error(
          "Não foi possível processar a imagem.",
        );
      }
    };


  /* ==========================================================
     EDITOR HELPERS
  ========================================================== */

  const updateEditTheme =
    (callback) => {
      setEditTheme(
        (current) =>
          ensureThemeStructure(
            callback(
              deepClone(
                current,
              ),
            ),
          ),
      );
    };


  const setToken =
    (
      key,
      value,
    ) => {
      updateEditTheme(
        (theme) => ({
          ...theme,
          [editMode]: {
            ...theme[
              editMode
            ],
            [key]: value,
          },
        }),
      );
    };


  const setThemeField =
    (
      section,
      key,
      value,
    ) => {
      updateEditTheme(
        (theme) => ({
          ...theme,
          [section]: {
            ...theme[
              section
            ],
            [key]: value,
          },
        }),
      );
    };


  const setRootField =
    (
      key,
      value,
    ) => {
      updateEditTheme(
        (theme) => ({
          ...theme,
          [key]: value,
        }),
      );
    };


  /* ==========================================================
     SAVE EDITOR
  ========================================================== */

  const saveEditorTheme =
    async () => {
      if (
        !editTheme?.name?.trim()
      ) {
        toast.error(
          "Dê um nome ao tema",
        );

        return;
      }

      setBusy(true);

      try {
        const normalized =
          ensureThemeStructure(
            editTheme,
          );

        const payload =
          buildThemePayload(
            normalized,
          );

        let saved;

        if (
          normalized.id &&
          !normalized.is_preset
        ) {
          saved = (
            await api.put(
              `/themes/${normalized.id}`,
              payload,
            )
          ).data;
        } else {
          saved = (
            await api.post(
              "/themes",
              payload,
            )
          ).data;
        }

        const finalTheme =
          ensureThemeStructure(
            saved,
          );

        setEditTheme(
          finalTheme,
        );

        startPreview(
          finalTheme,
        );

        toast.success(
          "Tema salvo na biblioteca",
        );

        await load();
      } catch (err) {
        toast.error(
          formatApiErrorDetail(
            err.response?.data?.detail,
          ),
        );
      } finally {
        setBusy(false);
      }
    };


  /* ==========================================================
     VALIDATE
  ========================================================== */

  const validateEditor =
    async () => {
      if (!editTheme) {
        return;
      }

      try {
        const normalized =
          ensureThemeStructure(
            editTheme,
          );

        const {
          data: validation,
        } = await api.post(
          "/themes/validate",
          buildThemePayload(
            normalized,
          ),
        );

        if (
          validation.issues
            ?.length === 0
        ) {
          toast.success(
            "Contraste aprovado — tema 100% legível",
          );

          setIssues(null);
        } else {
          setIssues(
            validation.issues,
          );
        }
      } catch (err) {
        toast.error(
          formatApiErrorDetail(
            err.response?.data?.detail,
          ),
        );
      }
    };


  /* ==========================================================
     AI DESIGNER
  ========================================================== */

  const generateAI =
    async () => {
      if (
        !aiPrompt.trim()
      ) {
        return;
      }

      setBusy(true);
      setAiProposal(null);

      try {
        const {
          data: response,
        } = await api.post(
          "/themes/ai-designer",
          {
            prompt:
              aiPrompt,
          },
        );

        const normalized =
          ensureThemeStructure(
            response.theme,
          );

        setAiProposal({
          ...response,
          theme:
            normalized,
        });

        toast.success(
          `Proposta "${normalized.name}" gerada pelo Designer IA`,
        );
      } catch (err) {
        toast.error(
          formatApiErrorDetail(
            err.response?.data?.detail,
          ),
        );
      } finally {
        setBusy(false);
      }
    };


  /* ==========================================================
     SAVE AI
  ========================================================== */

  const saveAIProposal =
    async () => {
      if (
        !aiProposal?.theme
      ) {
        return;
      }

      try {
        const theme =
          ensureThemeStructure(
            aiProposal.theme,
          );

        const {
          data: saved,
        } = await api.post(
          "/themes",
          buildThemePayload(
            {
              ...theme,
              category:
                "Personalizados",
            },
          ),
        );

        toast.success(
          "Tema da IA salvo na biblioteca",
        );

        await load();

        return saved;
      } catch (err) {
        toast.error(
          formatApiErrorDetail(
            err.response?.data?.detail,
          ),
        );
      }
    };


  /* ==========================================================
     OPEN EDITOR
  ========================================================== */

  const openEditor =
    (theme) => {
      const normalized =
        ensureThemeStructure(
          {
            ...theme,
            name:
              theme.is_preset
                ? `${theme.name} (personalizado)`
                : theme.name,
            id:
              theme.is_preset
                ? null
                : theme.id,
            is_preset: false,
          },
        );

      setEditTheme(
        normalized,
      );

      setEditorSection(
        "cores",
      );

      toast.info(
        "Tema carregado no Editor Visual",
      );
    };


  /* ==========================================================
     CATEGORIES
  ========================================================== */

  const categories = [
    ...new Set(
      data.themes.map(
        (theme) =>
          theme.category,
      ),
    ),
  ];

  const activeId =
    data.active_theme_id;


  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <div
      className="
        max-w-7xl
        mx-auto
        space-y-6
        pb-10
      "
      data-testid="visual-identity-page"
    >

      {/* ======================================================
          HEADER
      ====================================================== */}

      <div
        className="
          flex
          flex-col
          xl:flex-row
          xl:items-center
          justify-between
          gap-4
        "
      >
        <div>
          <h1
            className="
              font-heading
              text-2xl
              sm:text-3xl
              font-bold
              tracking-tight
              flex
              items-center
              gap-2
            "
          >
            <Palette
              size={27}
              className="text-primary"
            />

            Identidade Visual e Temas
          </h1>

          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Controle completo da experiência visual do ERP:
            cores, tipografia, navegação, componentes,
            espaçamento, efeitos, animações e identidade da marca.
          </p>
        </div>


        {previewing && (
          <div
            className="
              flex
              flex-wrap
              items-center
              gap-2
              rounded-xl
              border
              border-cyan-500/40
              bg-cyan-500/10
              px-3
              py-2
              shadow-sm
            "
            data-testid="preview-banner"
          >
            <Eye
              size={14}
              className="text-cyan-400"
            />

            <span className="text-xs">
              Pré-visualização:
              {" "}
              <b>
                {previewing.name ||
                  "editor"}
              </b>
            </span>

            {previewing.id && (
              <Button
                size="sm"
                className="h-7"
                onClick={() =>
                  publish(
                    previewing.id,
                  )
                }
                disabled={busy}
                data-testid="preview-publish-btn"
              >
                <Rocket
                  size={12}
                  className="mr-1"
                />

                Publicar
              </Button>
            )}

            <Button
              size="sm"
              variant="ghost"
              className="h-7"
              onClick={
                stopPreview
              }
              data-testid="preview-discard-btn"
            >
              Descartar
            </Button>
          </div>
        )}
      </div>


      {/* ======================================================
          TABS
      ====================================================== */}

      <Tabs defaultValue="biblioteca">

        <TabsList
          className="
            flex
            flex-wrap
            h-auto
            gap-1
          "
        >
          <TabsTrigger
            value="marca"
            data-testid="tab-marca"
          >
            <Palette
              size={13}
              className="mr-1.5"
            />
            Marca
          </TabsTrigger>

          <TabsTrigger
            value="biblioteca"
            data-testid="tab-biblioteca"
          >
            <Layers3
              size={13}
              className="mr-1.5"
            />
            Biblioteca
          </TabsTrigger>

          <TabsTrigger
            value="editor"
            data-testid="tab-editor"
          >
            <Settings2
              size={13}
              className="mr-1.5"
            />
            Editor Visual
          </TabsTrigger>

          <TabsTrigger
            value="ia"
            data-testid="tab-ia"
          >
            <Sparkles
              size={13}
              className="mr-1.5"
            />
            Designer IA
          </TabsTrigger>

          <TabsTrigger
            value="historico"
            data-testid="tab-historico"
          >
            <History
              size={13}
              className="mr-1.5"
            />
            Histórico
          </TabsTrigger>
        </TabsList>


        {/* ====================================================
            MARCA
        ==================================================== */}

        <TabsContent
          value="marca"
          className="
            mt-6
            grid
            lg:grid-cols-2
            gap-6
          "
        >

          <Card
            className="
              p-5
              border-border
              bg-card
              space-y-4
            "
          >
            <h3 className="font-heading font-semibold text-sm">
              Nomes e textos institucionais
            </h3>

            {[
              [
                "company_name",
                "Nome da empresa",
              ],
              [
                "system_name",
                "Nome do sistema",
              ],
              [
                "login_name",
                "Nome exibido no login",
              ],
              [
                "header_name",
                "Nome exibido no cabeçalho",
              ],
              [
                "browser_title",
                "Título da barra do navegador",
              ],
              [
                "login_message",
                "Mensagem institucional do login",
              ],
              [
                "support_email",
                "E-mail de suporte",
              ],
              [
                "support_phone",
                "Telefone de suporte",
              ],
              [
                "website",
                "Website",
              ],
            ].map(
              ([key, label]) => (
                <div
                  key={key}
                  className="space-y-1.5"
                >
                  <Label>
                    {label}
                  </Label>

                  <Input
                    value={
                      brandForm[
                        key
                      ] || ""
                    }
                    onChange={(
                      event,
                    ) =>
                      setBrandForm(
                        (
                          current,
                        ) => ({
                          ...current,
                          [key]:
                            event
                              .target
                              .value,
                        }),
                      )
                    }
                    data-testid={`brand-${key}-input`}
                  />
                </div>
              ),
            )}
          </Card>


          <Card
            className="
              p-5
              border-border
              bg-card
              space-y-4
            "
          >
            <h3 className="font-heading font-semibold text-sm">
              Logotipos e imagens
            </h3>

            {[
              [
                "favicon",
                "Ícone do navegador",
              ],
              [
                "logo_main",
                "Logotipo principal",
              ],
              [
                "logo_small",
                "Logotipo reduzido",
              ],
              [
                "login_bg",
                "Imagem de fundo do login",
              ],
            ].map(
              ([key, label]) => (
                <div
                  key={key}
                  className="
                    flex
                    items-center
                    gap-3
                  "
                >
                  {brandForm[key] ? (
                    <img
                      src={
                        brandForm[
                          key
                        ]
                      }
                      alt={label}
                      className="
                        h-10
                        w-10
                        rounded-md
                        object-cover
                        border
                        border-border
                      "
                    />
                  ) : (
                    <div
                      className="
                        h-10
                        w-10
                        rounded-md
                        border
                        border-dashed
                        border-border
                        flex
                        items-center
                        justify-center
                      "
                    >
                      <ImageIcon
                        size={14}
                        className="text-muted-foreground"
                      />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      {label}
                    </p>

                    <p className="text-[10px] text-muted-foreground">
                      Upload de imagem convertido automaticamente.
                    </p>
                  </div>

                  <input
                    ref={
                      uploadRefs[
                        key
                      ]
                    }
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(
                      event,
                    ) =>
                      uploadBrandImage(
                        key,
                      )(
                        event
                          .target
                          .files,
                      )
                    }
                    data-testid={`brand-${key}-file`}
                  />

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      uploadRefs[
                        key
                      ].current?.click()
                    }
                    data-testid={`brand-${key}-upload-btn`}
                  >
                    <Upload
                      size={12}
                      className="mr-1.5"
                    />
                    Enviar
                  </Button>

                  {brandForm[
                    key
                  ] && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setBrandForm(
                          (
                            current,
                          ) => ({
                            ...current,
                            [key]:
                              "",
                          }),
                        )
                      }
                    >
                      <Trash2
                        size={12}
                      />
                    </Button>
                  )}
                </div>
              ),
            )}

            <Button
              className="w-full"
              onClick={
                saveBranding
              }
              disabled={busy}
              data-testid="brand-save-btn"
            >
              <Save
                size={15}
                className="mr-2"
              />

              Publicar identidade da marca
            </Button>
          </Card>
        </TabsContent>


        {/* ====================================================
            BIBLIOTECA
        ==================================================== */}

        <TabsContent
          value="biblioteca"
          className="mt-6 space-y-6"
        >

          <div
            className="
              flex
              flex-wrap
              items-center
              gap-2
            "
          >
            <input
              ref={importRef}
              type="file"
              accept="application/json"
              hidden
              onChange={(event) =>
                importTheme(
                  event.target.files,
                )
              }
              data-testid="theme-import-input"
            />

            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                importRef.current?.click()
              }
              data-testid="theme-import-btn"
            >
              <Upload
                size={13}
                className="mr-1.5"
              />

              Importar tema
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={
                resetDefault
              }
              disabled={busy}
              data-testid="theme-reset-default-btn"
            >
              <RotateCcw
                size={13}
                className="mr-1.5"
              />

              Restaurar padrão
            </Button>
          </div>


          {categories.map(
            (category) => (
              <div
                key={category}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-semibold text-sm">
                    {category}
                  </h3>

                  <Badge
                    variant="outline"
                    className="text-[10px]"
                  >
                    {
                      data.themes.filter(
                        (theme) =>
                          theme.category ===
                          category,
                      ).length
                    }{" "}
                    temas
                  </Badge>
                </div>

                <div
                  className="
                    grid
                    grid-cols-1
                    sm:grid-cols-2
                    lg:grid-cols-3
                    xl:grid-cols-4
                    gap-4
                  "
                >
                  {data.themes
                    .filter(
                      (theme) =>
                        theme.category ===
                        category,
                    )
                    .map(
                      (theme) => (
                        <Card
                          key={
                            theme.id
                          }
                          className={`
                            group
                            p-4
                            border-border
                            bg-card
                            space-y-3
                            transition-all
                            duration-300
                            hover:-translate-y-0.5
                            hover:shadow-lg
                            ${
                              activeId ===
                              theme.id
                                ? "border-primary shadow-md shadow-primary/10"
                                : ""
                            }
                          `}
                          data-testid={`theme-card-${theme.id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">
                                {
                                  theme.name
                                }
                              </p>

                              {theme.description && (
                                <p className="text-[10px] text-muted-foreground line-clamp-2 mt-0.5">
                                  {
                                    theme.description
                                  }
                                </p>
                              )}
                            </div>

                            {activeId ===
                              theme.id && (
                              <Badge
                                className="
                                  bg-primary/15
                                  text-primary
                                  border-primary/30
                                "
                                variant="outline"
                                data-testid="active-theme-badge"
                              >
                                <CheckCircle2
                                  size={10}
                                  className="mr-1"
                                />
                                Ativo
                              </Badge>
                            )}
                          </div>


                          <ThemeMiniPreview
                            theme={
                              theme
                            }
                            mode="dark"
                          />

                          <div className="flex items-center justify-between">
                            <Swatches
                              theme={
                                theme
                              }
                            />

                            <div className="flex items-center gap-1 text-[9px] text-muted-foreground">
                              <Type size={10} />
                              {
                                theme.font_body ||
                                theme.typography?.font_body ||
                                "Inter"
                              }
                            </div>
                          </div>


                          <div className="flex flex-wrap gap-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() =>
                                startPreview(
                                  theme,
                                )
                              }
                              data-testid={`theme-preview-${theme.id}`}
                            >
                              <Eye
                                size={11}
                                className="mr-1"
                              />
                              Prévia
                            </Button>

                            <Button
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() =>
                                publish(
                                  theme.id,
                                )
                              }
                              disabled={
                                busy ||
                                activeId ===
                                  theme.id
                              }
                              data-testid={`theme-publish-${theme.id}`}
                            >
                              <Rocket
                                size={11}
                                className="mr-1"
                              />
                              Publicar
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs"
                              onClick={() =>
                                duplicate(
                                  theme.id,
                                )
                              }
                              title="Duplicar"
                            >
                              <Copy
                                size={11}
                              />
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs"
                              onClick={() =>
                                openEditor(
                                  theme,
                                )
                              }
                              title="Editar"
                            >
                              <Palette
                                size={11}
                              />
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs"
                              onClick={() =>
                                exportTheme(
                                  theme.id,
                                  theme.name,
                                )
                              }
                              title="Exportar"
                            >
                              <Download
                                size={11}
                              />
                            </Button>

                            {!theme.is_preset && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs text-destructive"
                                onClick={() =>
                                  removeTheme(
                                    theme.id,
                                  )
                                }
                                title="Excluir"
                              >
                                <Trash2
                                  size={11}
                                />
                              </Button>
                            )}
                          </div>
                        </Card>
                      ),
                    )}
                </div>
              </div>
            ),
          )}
        </TabsContent>


        {/* ====================================================
            EDITOR
        ==================================================== */}

        <TabsContent
          value="editor"
          className="mt-6"
        >

          {!editTheme ? (
            <Card
              className="
                p-10
                border-border
                bg-card
                text-center
                space-y-4
              "
            >
              <div
                className="
                  mx-auto
                  h-14
                  w-14
                  rounded-2xl
                  bg-primary/10
                  text-primary
                  flex
                  items-center
                  justify-center
                "
              >
                <Wand2
                  size={25}
                />
              </div>

              <div>
                <h3 className="font-heading font-semibold">
                  Editor Visual Profissional
                </h3>

                <p className="text-sm text-muted-foreground mt-1 max-w-xl mx-auto">
                  Escolha um tema da biblioteca e personalize
                  cores, tipografia, navegação, componentes,
                  efeitos, densidade e identidade.
                </p>
              </div>

              <div className="max-w-sm mx-auto">
                <Select
                  onValueChange={(
                    id,
                  ) => {
                    const theme =
                      data.themes.find(
                        (item) =>
                          item.id ===
                          id,
                      );

                    if (
                      theme
                    ) {
                      openEditor(
                        theme,
                      );
                    }
                  }}
                >
                  <SelectTrigger
                    data-testid="editor-base-select"
                  >
                    <SelectValue placeholder="Selecionar tema base..." />
                  </SelectTrigger>

                  <SelectContent>
                    {data.themes.map(
                      (theme) => (
                        <SelectItem
                          key={
                            theme.id
                          }
                          value={
                            theme.id
                          }
                        >
                          {theme.name}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>
            </Card>
          ) : (
            <div className="space-y-5">

              {/* ================================================
                  EDITOR HEADER
              ================================================ */}

              <Card
                className="
                  p-4
                  border-border
                  bg-card
                "
              >
                <div
                  className="
                    flex
                    flex-col
                    xl:flex-row
                    xl:items-end
                    gap-4
                  "
                >
                  <div className="space-y-1.5 flex-1 min-w-52">
                    <Label>
                      Nome do tema
                    </Label>

                    <Input
                      value={
                        editTheme.name ||
                        ""
                      }
                      onChange={(
                        event,
                      ) =>
                        setRootField(
                          "name",
                          event.target.value,
                        )
                      }
                      data-testid="editor-name-input"
                    />
                  </div>

                  <SelectField
                    label="Arredondamento geral"
                    value={
                      editTheme.radius
                    }
                    onChange={(
                      value,
                    ) => {
                      setRootField(
                        "radius",
                        value,
                      );

                      setThemeField(
                        "components",
                        "radius",
                        value,
                      );
                    }}
                    options={
                      RADIUS_OPTIONS
                    }
                    className="w-40"
                  />

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={
                        validateEditor
                      }
                    >
                      <CheckCircle2
                        size={14}
                        className="mr-1.5"
                      />
                      Validar
                    </Button>

                    <Button
                      onClick={
                        saveEditorTheme
                      }
                      disabled={busy}
                    >
                      <Save
                        size={14}
                        className="mr-1.5"
                      />
                      Salvar tema
                    </Button>
                  </div>
                </div>
              </Card>


              {/* ================================================
                  EDITOR SECTIONS
              ================================================ */}

              <Tabs
                value={
                  editorSection
                }
                onValueChange={
                  setEditorSection
                }
              >
                <TabsList
                  className="
                    flex
                    flex-wrap
                    h-auto
                  "
                >
                  <TabsTrigger value="cores">
                    <Palette
                      size={13}
                      className="mr-1.5"
                    />
                    Cores
                  </TabsTrigger>

                  <TabsTrigger value="tipografia">
                    <Type
                      size={13}
                      className="mr-1.5"
                    />
                    Tipografia
                  </TabsTrigger>

                  <TabsTrigger value="navegacao">
                    <PanelLeft
                      size={13}
                      className="mr-1.5"
                    />
                    Navegação
                  </TabsTrigger>

                  <TabsTrigger value="componentes">
                    <Box
                      size={13}
                      className="mr-1.5"
                    />
                    Componentes
                  </TabsTrigger>

                  <TabsTrigger value="efeitos">
                    <Zap
                      size={13}
                      className="mr-1.5"
                    />
                    Efeitos
                  </TabsTrigger>

                  <TabsTrigger value="densidade">
                    <Gauge
                      size={13}
                      className="mr-1.5"
                    />
                    Densidade
                  </TabsTrigger>

                  <TabsTrigger value="identidade">
                    <LayoutDashboard
                      size={13}
                      className="mr-1.5"
                    />
                    Identidade
                  </TabsTrigger>
                </TabsList>


                {/* ==============================================
                    CORES
                ============================================== */}

                <TabsContent
                  value="cores"
                  className="mt-4 space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading font-semibold text-sm">
                        Sistema de cores
                      </h3>

                      <p className="text-xs text-muted-foreground mt-0.5">
                        Cada modo possui sua própria paleta.
                      </p>
                    </div>

                    <Tabs
                      value={
                        editMode
                      }
                      onValueChange={
                        setEditMode
                      }
                    >
                      <TabsList>
                        <TabsTrigger value="dark">
                          <Moon
                            size={12}
                            className="mr-1.5"
                          />
                          Escuro
                        </TabsTrigger>

                        <TabsTrigger value="light">
                          <Sun
                            size={12}
                            className="mr-1.5"
                          />
                          Claro
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>


                  <div
                    className="
                      grid
                      md:grid-cols-2
                      xl:grid-cols-3
                      gap-4
                    "
                  >
                    {Object.entries(
                      TOKEN_GROUPS,
                    ).map(
                      ([
                        group,
                        keys,
                      ]) => (
                        <Card
                          key={
                            group
                          }
                          className="
                            p-4
                            border-border
                            bg-card
                          "
                        >
                          <h4 className="font-heading font-semibold text-xs mb-3 text-muted-foreground uppercase tracking-wider">
                            {group}
                          </h4>

                          <div className="space-y-2.5">
                            {keys.map(
                              (key) => (
                                <ColorField
                                  key={
                                    key
                                  }
                                  label={
                                    key
                                  }
                                  value={
                                    editTheme[
                                      editMode
                                    ]?.[
                                      key
                                    ]
                                  }
                                  onChange={(
                                    value,
                                  ) =>
                                    setToken(
                                      key,
                                      value,
                                    )
                                  }
                                />
                              ),
                            )}
                          </div>
                        </Card>
                      ),
                    )}


                    <Card className="p-4 border-border bg-card">
                      <h4 className="font-heading font-semibold text-xs mb-3 text-muted-foreground uppercase tracking-wider">
                        Contraste ao vivo
                      </h4>

                      {[
                        [
                          "foreground",
                          "background",
                          "Texto / Fundo",
                        ],
                        [
                          "primary_foreground",
                          "primary",
                          "Texto / Botão primário",
                        ],
                        [
                          "muted_foreground",
                          "card",
                          "Texto secundário / Card",
                        ],
                        [
                          "card_foreground",
                          "card",
                          "Texto do card",
                        ],
                      ].map(
                        ([
                          foreground,
                          background,
                          label,
                        ]) => {
                          const ratio =
                            editTheme[
                              editMode
                            ]?.[
                              foreground
                            ] &&
                            editTheme[
                              editMode
                            ]?.[
                              background
                            ]
                              ? contrastRatio(
                                  editTheme[
                                    editMode
                                  ][
                                    foreground
                                  ],
                                  editTheme[
                                    editMode
                                  ][
                                    background
                                  ],
                                )
                              : 0;

                          return (
                            <div
                              key={
                                label
                              }
                              className="
                                flex
                                items-center
                                justify-between
                                text-xs
                                py-1.5
                                border-b
                                border-border/40
                                last:border-0
                              "
                            >
                              <span>
                                {
                                  label
                                }
                              </span>

                              <Badge
                                variant="outline"
                                className={
                                  ratio >=
                                  4.5
                                    ? "text-emerald-400 border-emerald-500/40"
                                    : ratio >=
                                      3
                                      ? "text-amber-400 border-amber-500/40"
                                      : "text-red-400 border-red-500/40"
                                }
                              >
                                {
                                  ratio
                                }:1
                              </Badge>
                            </div>
                          );
                        },
                      )}

                      <p className="text-[10px] text-muted-foreground mt-2">
                        Mínimo recomendado:
                        4.5:1 para texto principal
                        e 3:1 para texto secundário.
                      </p>
                    </Card>
                  </div>
                </TabsContent>


                {/* ==============================================
                    TIPOGRAFIA
                ============================================== */}

                <TabsContent
                  value="tipografia"
                  className="mt-4"
                >
                  <div className="grid lg:grid-cols-2 gap-4">

                    <Card className="p-5 border-border bg-card space-y-4">
                      <div>
                        <h3 className="font-heading font-semibold text-sm">
                          Sistema tipográfico
                        </h3>

                        <p className="text-xs text-muted-foreground mt-1">
                          Controle independente para títulos,
                          textos, display e código.
                        </p>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <SelectField
                          label="Fonte dos títulos"
                          value={
                            editTheme.typography.font_heading
                          }
                          onChange={(
                            value,
                          ) => {
                            setRootField(
                              "font_heading",
                              value,
                            );

                            setThemeField(
                              "typography",
                              "font_heading",
                              value,
                            );

                            setThemeField(
                              "typography",
                              "font_display",
                              value,
                            );
                          }}
                          options={
                            FONTS
                          }
                        />

                        <SelectField
                          label="Fonte do texto"
                          value={
                            editTheme.typography.font_body
                          }
                          onChange={(
                            value,
                          ) => {
                            setRootField(
                              "font_body",
                              value,
                            );

                            setThemeField(
                              "typography",
                              "font_body",
                              value,
                            );
                          }}
                          options={
                            FONTS
                          }
                        />

                        <SelectField
                          label="Fonte mono"
                          value={
                            editTheme.typography.font_mono
                          }
                          onChange={(
                            value,
                          ) => {
                            setRootField(
                              "font_mono",
                              value,
                            );

                            setThemeField(
                              "typography",
                              "font_mono",
                              value,
                            );
                          }}
                          options={[
                            "JetBrains Mono",
                            "Fira Code",
                            "IBM Plex Mono",
                            "Roboto Mono",
                          ]}
                        />

                        <SelectField
                          label="Tamanho base"
                          value={
                            editTheme.typography.base_size
                          }
                          onChange={(
                            value,
                          ) =>
                            setThemeField(
                              "typography",
                              "base_size",
                              value,
                            )
                          }
                          options={
                            FONT_SIZE_OPTIONS
                          }
                        />
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <SelectField
                          label="Peso dos títulos"
                          value={String(
                            editTheme.typography.heading_weight,
                          )}
                          onChange={(
                            value,
                          ) =>
                            setThemeField(
                              "typography",
                              "heading_weight",
                              Number(
                                value,
                              ),
                            )
                          }
                          options={
                            FONT_WEIGHT_OPTIONS
                          }
                        />

                        <SelectField
                          label="Peso do texto"
                          value={String(
                            editTheme.typography.body_weight,
                          )}
                          onChange={(
                            value,
                          ) =>
                            setThemeField(
                              "typography",
                              "body_weight",
                              Number(
                                value,
                              ),
                            )
                          }
                          options={
                            FONT_WEIGHT_OPTIONS
                          }
                        />

                        <SelectField
                          label="Peso médio"
                          value={String(
                            editTheme.typography.medium_weight,
                          )}
                          onChange={(
                            value,
                          ) =>
                            setThemeField(
                              "typography",
                              "medium_weight",
                              Number(
                                value,
                              ),
                            )
                          }
                          options={
                            FONT_WEIGHT_OPTIONS
                          }
                        />

                        <SelectField
                          label="Peso semibold"
                          value={String(
                            editTheme.typography.semibold_weight,
                          )}
                          onChange={(
                            value,
                          ) =>
                            setThemeField(
                              "typography",
                              "semibold_weight",
                              Number(
                                value,
                              ),
                            )
                          }
                          options={
                            FONT_WEIGHT_OPTIONS
                          }
                        />
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label>
                            Espaçamento entre linhas
                          </Label>

                          <Input
                            value={
                              editTheme.typography.line_height
                            }
                            onChange={(
                              event,
                            ) =>
                              setThemeField(
                                "typography",
                                "line_height",
                                event
                                  .target
                                  .value,
                              )
                            }
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label>
                            Letter spacing
                          </Label>

                          <Input
                            value={
                              editTheme.typography.letter_spacing
                            }
                            onChange={(
                              event,
                            ) =>
                              setThemeField(
                                "typography",
                                "letter_spacing",
                                event
                                  .target
                                  .value,
                              )
                            }
                          />
                        </div>

                        <div className="space-y-1.5 sm:col-span-2">
                          <Label>
                            Letter spacing dos títulos
                          </Label>

                          <Input
                            value={
                              editTheme.typography.heading_letter_spacing
                            }
                            onChange={(
                              event,
                            ) =>
                              setThemeField(
                                "typography",
                                "heading_letter_spacing",
                                event
                                  .target
                                  .value,
                              )
                            }
                          />
                        </div>
                      </div>
                    </Card>


                    <Card className="p-5 border-border bg-card">
                      <h3 className="font-heading font-semibold text-sm mb-4">
                        Prévia tipográfica
                      </h3>

                      <div
                        className="rounded-xl border border-border p-5 space-y-3"
                        style={{
                          fontFamily:
                            editTheme.typography.font_body,
                        }}
                      >
                        <p
                          style={{
                            fontFamily:
                              editTheme.typography.font_heading,
                            fontWeight:
                              editTheme.typography.heading_weight,
                          }}
                          className="text-2xl"
                        >
                          Controle Intelligence
                        </p>

                        <p
                          className="text-sm text-muted-foreground"
                          style={{
                            lineHeight:
                              editTheme.typography.line_height,
                            letterSpacing:
                              editTheme.typography.letter_spacing,
                          }}
                        >
                          Uma interface corporativa
                          precisa ser bonita, mas
                          também precisa transmitir
                          confiança, clareza e controle.
                        </p>

                        <div
                          className="text-xs opacity-70"
                          style={{
                            fontFamily:
                              editTheme.typography.font_mono,
                          }}
                        >
                          protocol=AST-2026-000184
                        </div>
                      </div>
                    </Card>
                  </div>
                </TabsContent>


                {/* ==============================================
                    NAVEGAÇÃO
                ============================================== */}

                <TabsContent
                  value="navegacao"
                  className="mt-4"
                >
                  <div className="grid lg:grid-cols-2 gap-4">

                    <Card className="p-5 border-border bg-card space-y-4">
                      <div>
                        <h3 className="font-heading font-semibold text-sm">
                          Barra de navegação
                        </h3>

                        <p className="text-xs text-muted-foreground mt-1">
                          Defina a estrutura visual do menu
                          de Operação, Estrutura e demais módulos.
                        </p>
                      </div>

                      <SelectField
                        label="Tipo de navegação"
                        value={
                          editTheme.navigation.style
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "navigation",
                            "style",
                            value,
                          )
                        }
                        options={
                          NAVIGATION_STYLES
                        }
                      />

                      <SelectField
                        label="Estilo da sidebar"
                        value={
                          editTheme.navigation.sidebar_style
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "navigation",
                            "sidebar_style",
                            value,
                          )
                        }
                        options={
                          SIDEBAR_STYLES
                        }
                      />

                      <SelectField
                        label="Modo da sidebar"
                        value={
                          editTheme.navigation.sidebar_mode
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "navigation",
                            "sidebar_mode",
                            value,
                          )
                        }
                        options={[
                          {
                            value:
                              "expandida",
                            label:
                              "Expandida",
                          },
                          {
                            value:
                              "recolhida",
                            label:
                              "Recolhida",
                          },
                          {
                            value:
                              "hover",
                            label:
                              "Expande no hover",
                          },
                        ]}
                      />

                      <div className="grid sm:grid-cols-2 gap-4">
                        <SelectField
                          label="Largura da sidebar"
                          value={
                            editTheme.navigation.sidebar_width
                          }
                          onChange={(
                            value,
                          ) =>
                            setThemeField(
                              "navigation",
                              "sidebar_width",
                              value,
                            )
                          }
                          options={
                            WIDTH_OPTIONS
                          }
                        />

                        <SelectField
                          label="Largura recolhida"
                          value={
                            editTheme.navigation.sidebar_collapsed_width
                          }
                          onChange={(
                            value,
                          ) =>
                            setThemeField(
                              "navigation",
                              "sidebar_collapsed_width",
                              value,
                            )
                          }
                          options={[
                            "56px",
                            "64px",
                            "72px",
                            "80px",
                          ]}
                        />
                      </div>

                      <SelectField
                        label="Indicador do item ativo"
                        value={
                          editTheme.navigation.active_indicator
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "navigation",
                            "active_indicator",
                            value,
                          )
                        }
                        options={
                          ACTIVE_INDICATORS
                        }
                      />

                      <SelectField
                        label="Efeito ao passar o mouse"
                        value={
                          editTheme.navigation.hover_effect
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "navigation",
                            "hover_effect",
                            value,
                          )
                        }
                        options={
                          HOVER_EFFECTS
                        }
                      />
                    </Card>


                    <Card className="p-5 border-border bg-card space-y-4">
                      <div>
                        <h3 className="font-heading font-semibold text-sm">
                          Cabeçalho e recursos
                        </h3>
                      </div>

                      <SelectField
                        label="Estilo do cabeçalho"
                        value={
                          editTheme.navigation.header_style
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "navigation",
                            "header_style",
                            value,
                          )
                        }
                        options={
                          HEADER_STYLES
                        }
                      />

                      <SelectField
                        label="Altura do cabeçalho"
                        value={
                          editTheme.navigation.header_height
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "navigation",
                            "header_height",
                            value,
                          )
                        }
                        options={
                          HEADER_HEIGHT_OPTIONS
                        }
                      />

                      <div className="grid sm:grid-cols-2 gap-3">
                        <BooleanField
                          label="Mostrar ícones"
                          value={
                            editTheme.navigation.show_icons
                          }
                          onChange={(
                            value,
                          ) =>
                            setThemeField(
                              "navigation",
                              "show_icons",
                              value,
                            )
                          }
                        />

                        <BooleanField
                          label="Mostrar labels"
                          value={
                            editTheme.navigation.show_labels
                          }
                          onChange={(
                            value,
                          ) =>
                            setThemeField(
                              "navigation",
                              "show_labels",
                              value,
                            )
                          }
                        />

                        <BooleanField
                          label="Breadcrumbs"
                          value={
                            editTheme.navigation.show_breadcrumbs
                          }
                          onChange={(
                            value,
                          ) =>
                            setThemeField(
                              "navigation",
                              "show_breadcrumbs",
                              value,
                            )
                          }
                        />

                        <BooleanField
                          label="Busca"
                          value={
                            editTheme.navigation.show_search
                          }
                          onChange={(
                            value,
                          ) =>
                            setThemeField(
                              "navigation",
                              "show_search",
                              value,
                            )
                          }
                        />

                        <BooleanField
                          label="Notificações"
                          value={
                            editTheme.navigation.show_notifications
                          }
                          onChange={(
                            value,
                          ) =>
                            setThemeField(
                              "navigation",
                              "show_notifications",
                              value,
                            )
                          }
                        />

                        <BooleanField
                          label="Sidebar fixa"
                          value={
                            editTheme.navigation.sticky_sidebar
                          }
                          onChange={(
                            value,
                          ) =>
                            setThemeField(
                              "navigation",
                              "sticky_sidebar",
                              value,
                            )
                          }
                        />

                        <BooleanField
                          label="Cabeçalho fixo"
                          value={
                            editTheme.navigation.sticky_header
                          }
                          onChange={(
                            value,
                          ) =>
                            setThemeField(
                              "navigation",
                              "sticky_header",
                              value,
                            )
                          }
                        />
                      </div>
                    </Card>
                  </div>
                </TabsContent>


                {/* ==============================================
                    COMPONENTES
                ============================================== */}

                <TabsContent
                  value="componentes"
                  className="mt-4"
                >
                  <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-4">

                    <Card className="p-5 border-border bg-card space-y-4">
                      <h3 className="font-heading font-semibold text-sm">
                        Componentes principais
                      </h3>

                      <SelectField
                        label="Cards"
                        value={
                          editTheme.components.card_style
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "components",
                            "card_style",
                            value,
                          )
                        }
                        options={
                          CARD_STYLES
                        }
                      />

                      <SelectField
                        label="Botões"
                        value={
                          editTheme.components.button_style
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "components",
                            "button_style",
                            value,
                          )
                        }
                        options={
                          BUTTON_STYLES
                        }
                      />

                      <SelectField
                        label="Campos"
                        value={
                          editTheme.components.input_style
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "components",
                            "input_style",
                            value,
                          )
                        }
                        options={
                          INPUT_STYLES
                        }
                      />

                      <SelectField
                        label="Tabelas"
                        value={
                          editTheme.components.table_style
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "components",
                            "table_style",
                            value,
                          )
                        }
                        options={
                          TABLE_STYLES
                        }
                      />
                    </Card>


                    <Card className="p-5 border-border bg-card space-y-4">
                      <h3 className="font-heading font-semibold text-sm">
                        Arredondamentos
                      </h3>

                      <SelectField
                        label="Card"
                        value={
                          editTheme.components.card_radius
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "components",
                            "card_radius",
                            value,
                          )
                        }
                        options={
                          RADIUS_OPTIONS
                        }
                      />

                      <SelectField
                        label="Botão"
                        value={
                          editTheme.components.button_radius
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "components",
                            "button_radius",
                            value,
                          )
                        }
                        options={
                          RADIUS_OPTIONS
                        }
                      />

                      <SelectField
                        label="Input"
                        value={
                          editTheme.components.input_radius
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "components",
                            "input_radius",
                            value,
                          )
                        }
                        options={
                          RADIUS_OPTIONS
                        }
                      />

                      <SelectField
                        label="Modal"
                        value={
                          editTheme.components.modal_radius
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "components",
                            "modal_radius",
                            value,
                          )
                        }
                        options={
                          RADIUS_OPTIONS
                        }
                      />
                    </Card>


                    <Card className="p-5 border-border bg-card space-y-4">
                      <h3 className="font-heading font-semibold text-sm">
                        Acabamento
                      </h3>

                      <SelectField
                        label="Largura da borda"
                        value={
                          editTheme.components.border_width
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "components",
                            "border_width",
                            value,
                          )
                        }
                        options={[
                          "0px",
                          "1px",
                          "1.5px",
                          "2px",
                        ]}
                      />

                      <SelectField
                        label="Foco"
                        value={
                          editTheme.components.focus_width
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "components",
                            "focus_width",
                            value,
                          )
                        }
                        options={[
                          "1px",
                          "2px",
                          "3px",
                          "4px",
                        ]}
                      />

                      <SelectField
                        label="Badge"
                        value={
                          editTheme.components.badge_radius
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "components",
                            "badge_radius",
                            value,
                          )
                        }
                        options={[
                          "0.25rem",
                          "0.375rem",
                          "0.5rem",
                          "0.75rem",
                          "9999px",
                        ]}
                      />
                    </Card>
                  </div>
                </TabsContent>


                {/* ==============================================
                    EFEITOS
                ============================================== */}

                <TabsContent
                  value="efeitos"
                  className="mt-4"
                >
                  <div className="grid lg:grid-cols-2 gap-4">

                    <Card className="p-5 border-border bg-card space-y-4">
                      <div>
                        <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
                          <Zap
                            size={15}
                            className="text-primary"
                          />
                          Animações e transições
                        </h3>

                        <p className="text-xs text-muted-foreground mt-1">
                          Define a personalidade e fluidez
                          geral da interface.
                        </p>
                      </div>

                      <BooleanField
                        label="Animações ativadas"
                        description="Ativa transições e microinterações."
                        value={
                          editTheme.effects.animation_enabled
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "effects",
                            "animation_enabled",
                            value,
                          )
                        }
                      />

                      <SelectField
                        label="Velocidade das transições"
                        value={
                          editTheme.effects.animation_speed
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "effects",
                            "animation_speed",
                            value,
                          )
                        }
                        options={
                          ANIMATION_SPEED_OPTIONS
                        }
                      />

                      <BooleanField
                        label="Elevação no hover"
                        value={
                          editTheme.effects.hover_lift
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "effects",
                            "hover_lift",
                            value,
                          )
                        }
                      />

                      <BooleanField
                        label="Escala no hover"
                        value={
                          editTheme.effects.hover_scale
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "effects",
                            "hover_scale",
                            value,
                          )
                        }
                      />
                    </Card>


                    <Card className="p-5 border-border bg-card space-y-4">
                      <h3 className="font-heading font-semibold text-sm">
                        Glassmorphism e profundidade
                      </h3>

                      <BooleanField
                        label="Glassmorphism"
                        description="Aplica transparência e blur em superfícies."
                        value={
                          editTheme.effects.glass_enabled
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "effects",
                            "glass_enabled",
                            value,
                          )
                        }
                      />

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label>
                            Blur
                          </Label>

                          <Input
                            value={
                              editTheme.effects.glass_blur
                            }
                            onChange={(
                              event,
                            ) =>
                              setThemeField(
                                "effects",
                                "glass_blur",
                                event.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label>
                            Opacidade
                          </Label>

                          <Input
                            type="number"
                            min="0"
                            max="1"
                            step="0.01"
                            value={
                              editTheme.effects.glass_opacity
                            }
                            onChange={(
                              event,
                            ) =>
                              setThemeField(
                                "effects",
                                "glass_opacity",
                                Number(
                                  event.target.value,
                                ),
                              )
                            }
                          />
                        </div>
                      </div>

                      <BooleanField
                        label="Gradientes"
                        value={
                          editTheme.effects.gradient_enabled
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "effects",
                            "gradient_enabled",
                            value,
                          )
                        }
                      />

                      <BooleanField
                        label="Backdrop"
                        value={
                          editTheme.effects.backdrop_enabled
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "effects",
                            "backdrop_enabled",
                            value,
                          )
                        }
                      />

                      <SelectField
                        label="Sombra principal"
                        value={
                          editTheme.effects.shadow
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "effects",
                            "shadow",
                            value,
                          )
                        }
                        options={
                          SHADOW_OPTIONS
                        }
                      />

                      <SelectField
                        label="Sombra dos cards"
                        value={
                          editTheme.effects.card_shadow
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "effects",
                            "card_shadow",
                            value,
                          )
                        }
                        options={
                          SHADOW_OPTIONS
                        }
                      />
                    </Card>
                  </div>
                </TabsContent>


                {/* ==============================================
                    DENSIDADE
                ============================================== */}

                <TabsContent
                  value="densidade"
                  className="mt-4"
                >
                  <div className="grid lg:grid-cols-2 gap-4">

                    <Card className="p-5 border-border bg-card space-y-4">
                      <h3 className="font-heading font-semibold text-sm">
                        Densidade da interface
                      </h3>

                      <SelectField
                        label="Modo"
                        value={
                          editTheme.density.mode
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "density",
                            "mode",
                            value,
                          )
                        }
                        options={
                          DENSITY_OPTIONS
                        }
                      />

                      <div className="grid sm:grid-cols-2 gap-4">
                        {[
                          [
                            "page_padding",
                            "Padding das páginas",
                          ],
                          [
                            "section_gap",
                            "Espaço entre seções",
                          ],
                          [
                            "card_padding",
                            "Padding dos cards",
                          ],
                          [
                            "table_row_height",
                            "Altura das linhas",
                          ],
                          [
                            "form_gap",
                            "Espaço dos formulários",
                          ],
                          [
                            "menu_gap",
                            "Espaço do menu",
                          ],
                        ].map(
                          ([
                            key,
                            label,
                          ]) => (
                            <div
                              key={
                                key
                              }
                              className="space-y-1.5"
                            >
                              <Label>
                                {
                                  label
                                }
                              </Label>

                              <Input
                                value={
                                  editTheme.density[
                                    key
                                  ]
                                }
                                onChange={(
                                  event,
                                ) =>
                                  setThemeField(
                                    "density",
                                    key,
                                    event
                                      .target
                                      .value,
                                  )
                                }
                              />
                            </div>
                          ),
                        )}
                      </div>
                    </Card>


                    <Card className="p-5 border-border bg-card">
                      <h3 className="font-heading font-semibold text-sm mb-4">
                        Perfil de densidade
                      </h3>

                      <div className="space-y-3">
                        {[
                          [
                            "compacta",
                            "Compacta",
                            "Mais informação na tela. Ideal para operação intensa.",
                          ],
                          [
                            "confortavel",
                            "Confortável",
                            "Equilíbrio entre produtividade e legibilidade.",
                          ],
                          [
                            "espacosa",
                            "Espaçosa",
                            "Mais respiro visual e aparência premium.",
                          ],
                        ].map(
                          ([
                            value,
                            label,
                            description,
                          ]) => (
                            <button
                              key={
                                value
                              }
                              type="button"
                              onClick={() =>
                                setThemeField(
                                  "density",
                                  "mode",
                                  value,
                                )
                              }
                              className={`
                                w-full
                                text-left
                                rounded-xl
                                border
                                p-4
                                transition-all
                                ${
                                  editTheme.density.mode ===
                                  value
                                    ? "border-primary bg-primary/5 shadow-sm"
                                    : "border-border hover:border-primary/30"
                                }
                              `}
                            >
                              <p className="text-sm font-medium">
                                {
                                  label
                                }
                              </p>

                              <p className="text-xs text-muted-foreground mt-1">
                                {
                                  description
                                }
                              </p>
                            </button>
                          ),
                        )}
                      </div>
                    </Card>
                  </div>
                </TabsContent>


                {/* ==============================================
                    IDENTIDADE
                ============================================== */}

                <TabsContent
                  value="identidade"
                  className="mt-4"
                >
                  <div className="grid lg:grid-cols-2 gap-4">

                    <Card className="p-5 border-border bg-card space-y-4">
                      <h3 className="font-heading font-semibold text-sm">
                        Personalidade visual
                      </h3>

                      <SelectField
                        label="Estilo visual"
                        value={
                          editTheme.identity.visual_style
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "identity",
                            "visual_style",
                            value,
                          )
                        }
                        options={
                          VISUAL_STYLES
                        }
                      />

                      <SelectField
                        label="Presença da marca"
                        value={
                          editTheme.identity.brand_emphasis
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "identity",
                            "brand_emphasis",
                            value,
                          )
                        }
                        options={
                          BRAND_EMPHASIS
                        }
                      />

                      <SelectField
                        label="Posição do logo"
                        value={
                          editTheme.identity.logo_position
                        }
                        onChange={(
                          value,
                        ) =>
                          setThemeField(
                            "identity",
                            "logo_position",
                            value,
                          )
                        }
                        options={[
                          {
                            value:
                              "sidebar",
                            label:
                              "Sidebar",
                          },
                          {
                            value:
                              "header",
                            label:
                              "Cabeçalho",
                          },
                          {
                            value:
                              "both",
                            label:
                              "Sidebar + cabeçalho",
                          },
                        ]}
                      />

                      <div className="space-y-1.5">
                        <Label>
                          Tamanho do logo
                        </Label>

                        <Input
                          value={
                            editTheme.identity.logo_size
                          }
                          onChange={(
                            event,
                          ) =>
                            setThemeField(
                              "identity",
                              "logo_size",
                              event.target.value,
                            )
                          }
                        />
                      </div>
                    </Card>


                    <Card className="p-5 border-border bg-card space-y-4">
                      <h3 className="font-heading font-semibold text-sm">
                        Fundo da experiência
                      </h3>

                      <div className="space-y-1.5">
                        <Label>
                          Imagem de fundo
                        </Label>

                        <Input
                          value={
                            editTheme.identity.background_image
                          }
                          onChange={(
                            event,
                          ) =>
                            setThemeField(
                              "identity",
                              "background_image",
                              event.target.value,
                            )
                          }
                          placeholder="URL ou data:image/..."
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label>
                          Padrão / textura
                        </Label>

                        <Input
                          value={
                            editTheme.identity.background_pattern
                          }
                          onChange={(
                            event,
                          ) =>
                            setThemeField(
                              "identity",
                              "background_pattern",
                              event.target.value,
                            )
                          }
                          placeholder="Nome, URL ou configuração do padrão"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label>
                          Overlay
                        </Label>

                        <Input
                          type="number"
                          min="0"
                          max="1"
                          step="0.01"
                          value={
                            editTheme.identity.background_overlay
                          }
                          onChange={(
                            event,
                          ) =>
                            setThemeField(
                              "identity",
                              "background_overlay",
                              Number(
                                event.target.value,
                              ),
                            )
                          }
                        />
                      </div>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </TabsContent>


        {/* ====================================================
            IA
        ==================================================== */}

        <TabsContent
          value="ia"
          className="
            mt-6
            max-w-4xl
            space-y-4
          "
        >

          <Card
            className="
              p-6
              border-border
              bg-card
              space-y-5
            "
          >
            <div>
              <h3
                className="
                  font-heading
                  font-semibold
                  text-sm
                  flex
                  items-center
                  gap-2
                "
              >
                <Sparkles
                  size={16}
                  className="text-primary"
                />

                Designer IA
              </h3>

              <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                Descreva a experiência visual desejada.
                A IA pode gerar não apenas a paleta,
                mas também tipografia, navegação,
                componentes, efeitos, densidade e
                personalidade visual.
              </p>
            </div>


            <div className="flex flex-wrap gap-2">
              {AI_EXAMPLES.map(
                (example) => (
                  <button
                    key={
                      example
                    }
                    onClick={() =>
                      setAiPrompt(
                        example,
                      )
                    }
                    className="
                      text-[11px]
                      px-3
                      py-1.5
                      rounded-full
                      border
                      border-border
                      hover:border-primary/50
                      hover:bg-primary/5
                      transition-all
                      text-left
                    "
                    data-testid="ai-example-chip"
                  >
                    {example}
                  </button>
                ),
              )}
            </div>


            <Textarea
              rows={5}
              value={
                aiPrompt
              }
              onChange={(
                event,
              ) =>
                setAiPrompt(
                  event.target
                    .value,
                )
              }
              placeholder='Ex.: "Criar uma identidade florestal premium, verde profundo, madeira escura, detalhes dourados, sidebar glass e transições cinematográficas suaves..."'
              className="resize-none"
              data-testid="ai-designer-prompt"
            />


            <Button
              onClick={
                generateAI
              }
              disabled={
                busy ||
                !aiPrompt.trim()
              }
              data-testid="ai-designer-generate-btn"
            >
              <Sparkles
                size={14}
                className="mr-2"
              />

              {busy
                ? "Gerando proposta..."
                : "Gerar identidade com IA"}
            </Button>
          </Card>


          {aiProposal && (
            <Card
              className="
                p-5
                border-primary/40
                bg-card
                space-y-4
              "
              data-testid="ai-proposal-card"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <p className="font-heading font-semibold">
                    {
                      aiProposal
                        .theme
                        .name
                    }
                  </p>

                  <p className="text-xs text-muted-foreground mt-1">
                    {
                      aiProposal
                        .theme
                        .description ||
                      "Identidade gerada pelo Designer IA."
                    }
                  </p>
                </div>

                <Swatches
                  theme={
                    aiProposal.theme
                  }
                />
              </div>


              <ThemeMiniPreview
                theme={
                  aiProposal.theme
                }
                mode="dark"
              />


              <div className="grid sm:grid-cols-2 gap-3 text-xs">
                <div className="rounded-lg border border-border p-3">
                  <span className="text-muted-foreground">
                    Títulos
                  </span>

                  <p className="font-medium mt-1">
                    {
                      aiProposal
                        .theme
                        .typography
                        ?.font_heading ||
                      aiProposal
                        .theme
                        .font_heading
                    }
                  </p>
                </div>

                <div className="rounded-lg border border-border p-3">
                  <span className="text-muted-foreground">
                    Texto
                  </span>

                  <p className="font-medium mt-1">
                    {
                      aiProposal
                        .theme
                        .typography
                        ?.font_body ||
                      aiProposal
                        .theme
                        .font_body
                    }
                  </p>
                </div>

                <div className="rounded-lg border border-border p-3">
                  <span className="text-muted-foreground">
                    Navegação
                  </span>

                  <p className="font-medium mt-1">
                    {
                      aiProposal
                        .theme
                        .navigation
                        ?.style
                    }{" "}
                    ·{" "}
                    {
                      aiProposal
                        .theme
                        .navigation
                        ?.sidebar_style
                    }
                  </p>
                </div>

                <div className="rounded-lg border border-border p-3">
                  <span className="text-muted-foreground">
                    Efeitos
                  </span>

                  <p className="font-medium mt-1">
                    {aiProposal
                      .theme
                      .effects
                      ?.glass_enabled
                      ? "Glass"
                      : "Clássico"}

                    {" · "}

                    {aiProposal
                      .theme
                      .effects
                      ?.gradient_enabled
                      ? "Gradiente"
                      : "Sem gradiente"}
                  </p>
                </div>
              </div>


              <p className="text-xs text-muted-foreground">
                {aiProposal.validation
                  ?.valid
                  ? "✓ Contraste aprovado"
                  : `${aiProposal.validation?.blocking || 0} problema(s) de contraste`}
              </p>


              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    startPreview(
                      aiProposal.theme,
                    )
                  }
                  data-testid="ai-proposal-preview-btn"
                >
                  <Eye
                    size={12}
                    className="mr-1.5"
                  />
                  Pré-visualizar
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    openEditor(
                      aiProposal.theme,
                    );
                  }}
                >
                  <Settings2
                    size={12}
                    className="mr-1.5"
                  />
                  Abrir no editor
                </Button>

                <Button
                  size="sm"
                  onClick={
                    saveAIProposal
                  }
                  data-testid="ai-proposal-save-btn"
                >
                  <Save
                    size={12}
                    className="mr-1.5"
                  />
                  Salvar na biblioteca
                </Button>
              </div>
            </Card>
          )}
        </TabsContent>


        {/* ====================================================
            HISTÓRICO
        ==================================================== */}

        <TabsContent
          value="historico"
          className="
            mt-6
            max-w-3xl
          "
        >
          <Card
            className="
              border-border
              bg-card
              divide-y
              divide-border/50
            "
          >
            {[
              ...(data.history || []),
            ]
              .reverse()
              .map(
                (
                  history,
                  index,
                ) => (
                  <div
                    key={
                      index
                    }
                    className="
                      flex
                      flex-col
                      sm:flex-row
                      sm:items-center
                      justify-between
                      gap-3
                      px-4
                      py-4
                    "
                    data-testid="theme-history-entry"
                  >
                    <div>
                      <p
                        className="
                          text-sm
                          font-medium
                          flex
                          items-center
                          gap-2
                        "
                      >
                        <History
                          size={13}
                          className="text-muted-foreground"
                        />

                        {
                          history.theme_name
                        }
                      </p>

                      <p className="text-xs text-muted-foreground mt-0.5">
                        Publicado por{" "}
                        {
                          history.published_by
                        }{" "}
                        em{" "}
                        {new Date(
                          history.published_at,
                        ).toLocaleString(
                          "pt-BR",
                        )}
                      </p>
                    </div>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        history.theme_id
                          ? publish(
                              history.theme_id,
                            )
                          : resetDefault()
                      }
                      disabled={
                        busy
                      }
                      data-testid={`history-restore-${index}`}
                    >
                      <RotateCcw
                        size={12}
                        className="mr-1.5"
                      />

                      Restaurar
                    </Button>
                  </div>
                ),
              )}

            {(
              !data.history ||
              data.history.length ===
                0
            ) && (
              <p className="text-sm text-muted-foreground p-8 text-center">
                Nenhuma publicação de tema ainda.
              </p>
            )}
          </Card>
        </TabsContent>
      </Tabs>


      {/* ======================================================
          CONTRAST DIALOG
      ====================================================== */}

      <Dialog
        open={!!issues}
        onOpenChange={(
          open,
        ) =>
          !open &&
          setIssues(null)
        }
      >
        <DialogContent
          data-testid="contrast-issues-dialog"
        >
          <DialogHeader>
            <DialogTitle
              className="
                font-heading
                flex
                items-center
                gap-2
                text-amber-400
              "
            >
              <AlertTriangle
                size={17}
              />

              Validação de contraste
            </DialogTitle>
          </DialogHeader>

          <div
            className="
              space-y-2
              max-h-80
              overflow-y-auto
              scrollbar-thin
            "
          >
            {(
              issues || []
            ).map(
              (
                issue,
                index,
              ) => (
                <div
                  key={
                    index
                  }
                  className={`
                    rounded-md
                    border
                    px-3
                    py-2
                    text-xs
                    ${
                      issue.level ===
                      "aviso"
                        ? "border-amber-500/40 bg-amber-500/5"
                        : "border-red-500/40 bg-red-500/5"
                    }
                  `}
                >
                  <p className="font-medium">
                    {
                      issue.label
                    }{" "}
                    (
                    {issue.mode ===
                    "dark"
                      ? "modo escuro"
                      : "modo claro"}
                    ) —{" "}
                    {
                      issue.ratio
                    }
                    :1

                    <Badge
                      variant="outline"
                      className="ml-1 text-[9px]"
                    >
                      {
                        issue.level
                      }
                    </Badge>
                  </p>

                  <p className="text-muted-foreground mt-0.5">
                    {
                      issue.suggestion
                    }
                  </p>
                </div>
              ),
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}